import type { GameV1 } from '@/api/games';
import { POPULARITY_PAGE_SIZE, WAGERING_HEAD_SIZE } from './wagering';

export type VolatilityBucket = 'L' | 'M' | 'H';

const VOLATILITY_BUCKETS: Record<string, VolatilityBucket> = {
  low: 'L',
  'low-medium': 'L',
  medium: 'M',
  'medium-high': 'M',
  high: 'H',
  'very-high': 'H',
};

const PATTERN_LENGTH = 21;

const PATTERN_POOL_SIZE = WAGERING_HEAD_SIZE + POPULARITY_PAGE_SIZE;

const toBuckets = (raw: string) =>
  raw
    .replace(/[^lmh]/gi, '')
    .toUpperCase()
    .split('') as VolatilityBucket[];

const bucketOf = (game: GameV1): VolatilityBucket | undefined =>
  game.volatilityRating ? VOLATILITY_BUCKETS[game.volatilityRating] : undefined;

export const parseVolatilityPattern = (
  raw?: string
): VolatilityBucket[] | undefined => {
  const buckets = toBuckets(raw ?? '');

  if (buckets.length === PATTERN_LENGTH) return buckets;

  console.warn(
    `grid_games must hold ${PATTERN_LENGTH} L/M/H letters, got ${buckets.length}. Skipping the volatility pattern.`
  );

  return;
};

export const applyVolatilityPattern = (
  games: GameV1[],
  pattern: VolatilityBucket[]
): GameV1[] => {
  if (games.length < pattern.length) return games;

  const pool = games.slice(0, PATTERN_POOL_SIZE);
  const beyondPool = games.slice(PATTERN_POOL_SIZE);
  const head = pool.slice(0, WAGERING_HEAD_SIZE);
  const rest = pool.slice(WAGERING_HEAD_SIZE);
  const taken = new Set<string>();

  const takeFrom = (candidates: GameV1[], bucket: VolatilityBucket) => {
    const match = candidates.find(
      (game) => !taken.has(game.id) && bucketOf(game) === bucket
    );
    if (match) taken.add(match.id);
    return match;
  };

  const placed = pattern.map(
    (bucket) => takeFrom(head, bucket) ?? takeFrom(rest, bucket)
  );

  const leftovers = pool.filter((game) => !taken.has(game.id));
  const filled = placed.map((game) => {
    if (game) return game;
    const fallback = leftovers.shift();
    if (fallback) taken.add(fallback.id);
    return fallback;
  });

  return [
    ...filled.filter((game): game is GameV1 => Boolean(game)),
    ...leftovers,
    ...beyondPool,
  ];
};
